import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BundleConversionStats {
  bundle_id: string;
  bundle_name: string;
  total_deals: number;
  won_deals: number;
  lost_deals: number;
  open_deals: number;
  conversion_rate: number;
  total_won_revenue: number;
  avg_won_deal_value: number;
}

export function useBundleConversionStats() {
  return useQuery({
    queryKey: ['bundle-conversion-stats'],
    queryFn: async () => {
      // Get all line items with bundle_id and their deal status
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('deal_line_items')
        .select(`
          bundle_id,
          total_price,
          deal:deals(id, status, amount)
        `)
        .not('bundle_id', 'is', null);

      if (lineItemsError) throw lineItemsError;

      // Get all bundles for name mapping
      const { data: bundles, error: bundlesError } = await supabase
        .from('product_bundles')
        .select('id, name');

      if (bundlesError) throw bundlesError;

      const bundleMap = new Map(bundles?.map(b => [b.id, b.name]) || []);

      // Aggregate stats by bundle
      const statsMap = new Map<string, {
        bundle_id: string;
        bundle_name: string;
        deal_ids: Set<string>;
        won_deal_ids: Set<string>;
        lost_deal_ids: Set<string>;
        open_deal_ids: Set<string>;
        won_revenue: number;
      }>();

      lineItems?.forEach((item: any) => {
        if (!item.bundle_id || !item.deal) return;

        const dealId = item.deal.id;
        const dealStatus = item.deal.status;
        const dealAmount = item.deal.amount || 0;

        const existing = statsMap.get(item.bundle_id);

        if (existing) {
          if (!existing.deal_ids.has(dealId)) {
            existing.deal_ids.add(dealId);
            if (dealStatus === 'won') {
              existing.won_deal_ids.add(dealId);
              existing.won_revenue += dealAmount;
            } else if (dealStatus === 'lost') {
              existing.lost_deal_ids.add(dealId);
            } else {
              existing.open_deal_ids.add(dealId);
            }
          }
        } else {
          const newEntry = {
            bundle_id: item.bundle_id,
            bundle_name: bundleMap.get(item.bundle_id) || 'Unknown Bundle',
            deal_ids: new Set<string>([dealId]),
            won_deal_ids: new Set<string>(),
            lost_deal_ids: new Set<string>(),
            open_deal_ids: new Set<string>(),
            won_revenue: 0,
          };
          
          if (dealStatus === 'won') {
            newEntry.won_deal_ids.add(dealId);
            newEntry.won_revenue = dealAmount;
          } else if (dealStatus === 'lost') {
            newEntry.lost_deal_ids.add(dealId);
          } else {
            newEntry.open_deal_ids.add(dealId);
          }
          
          statsMap.set(item.bundle_id, newEntry);
        }
      });

      // Calculate conversion rates
      const stats: BundleConversionStats[] = Array.from(statsMap.values()).map(s => {
        const totalDeals = s.deal_ids.size;
        const wonDeals = s.won_deal_ids.size;
        const closedDeals = wonDeals + s.lost_deal_ids.size;
        
        return {
          bundle_id: s.bundle_id,
          bundle_name: s.bundle_name,
          total_deals: totalDeals,
          won_deals: wonDeals,
          lost_deals: s.lost_deal_ids.size,
          open_deals: s.open_deal_ids.size,
          conversion_rate: closedDeals > 0 ? (wonDeals / closedDeals) * 100 : 0,
          total_won_revenue: s.won_revenue,
          avg_won_deal_value: wonDeals > 0 ? s.won_revenue / wonDeals : 0,
        };
      });

      return stats.sort((a, b) => b.conversion_rate - a.conversion_rate);
    },
  });
}
